import {reportIpmAction, type IpmMetricTags} from '@github-ui/ipm-telemetry'
import {Button, Link} from '@primer/react'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {announce} from '@github-ui/aria-live'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {IpmBannerBase} from '../ipm-banner-base/IpmBannerBase'

export interface CopilotBudgetRequestBannerAnalytics {
  visibleEvent: string
  dismissEvent: string
  clickEvent: string
  location: string
}

export type BannerState = 'pre_request' | 'post_request' | 'request_denied'

export interface CopilotBudgetRequestBannerProps {
  isPersonalBudget: boolean
  organizationName: string
  resetDate: string
  requestUrl: string
  requestedAmountCents: number
  removeRequestUrl?: string
  contactUrl: string
  dismissPaths: Record<BannerState, string>
  bannerState: BannerState
  declinedDate?: string
  analytics: CopilotBudgetRequestBannerAnalytics
  rounded?: boolean
}

// Append the interaction's page + element to a URL's query string. They ride on the query
// (not a JSON body) because the backend reads params[:display_location]/[:surface] in its
// instrumentation around-action, before parse_json_params merges the request's JSON body.
function appendInteractionTags(url: string, tags: IpmMetricTags): string {
  const query = new URLSearchParams()
  if (tags.display_location) query.set('display_location', tags.display_location)
  if (tags.surface) query.set('surface', tags.surface)
  const queryString = query.toString()
  return queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url
}

export function CopilotBudgetRequestBanner({
  isPersonalBudget,
  organizationName,
  resetDate,
  requestUrl,
  requestedAmountCents,
  removeRequestUrl,
  contactUrl,
  dismissPaths,
  bannerState: initialBannerState,
  declinedDate,
  analytics,
  rounded,
}: CopilotBudgetRequestBannerProps) {
  const [bannerState, setBannerState] = useState<BannerState>(initialBannerState)
  const [submitting, setSubmitting] = useState(false)
  const bannerRef = useRef<HTMLDivElement>(null)
  const isInitialRenderRef = useRef(true)
  const dismissPath = dismissPaths[bannerState]
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'CopilotBudgetRequestBanner',
      surface: 'copilot_budget_request_banner',
      display_location: analytics.location,
      category: 'copilot_budget_request_banner',
      nudge_id: 'copilot_premium_request_budget',
      variant: bannerState,
    }),
    [analytics.location, bannerState],
  )

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }
    bannerRef.current?.focus()
  }, [bannerState])

  const handleRequestClick = useCallback(async () => {
    reportIpmAction('cta_click', telemetryTags)
    setSubmitting(true)
    try {
      const response = await verifiedFetch(appendInteractionTags(requestUrl, telemetryTags), {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({requested_amount_cents: requestedAmountCents}),
      })
      if (response.ok) {
        setBannerState('post_request')
        announce('Request sent. Your org admins will be notified.')
      }
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Network error — keep current state
    } finally {
      setSubmitting(false)
    }
  }, [requestUrl, requestedAmountCents, telemetryTags])

  const handleRemoveRequestClick = useCallback(async () => {
    if (!removeRequestUrl) return
    reportIpmAction('cta_click', telemetryTags)
    setSubmitting(true)
    try {
      const response = await verifiedFetch(appendInteractionTags(removeRequestUrl, telemetryTags), {method: 'DELETE'})
      if (response.ok) {
        setBannerState('pre_request')
        announce('Request removed.')
      }
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Network error — keep current state
    } finally {
      setSubmitting(false)
    }
  }, [removeRequestUrl, telemetryTags])

  if (bannerState === 'post_request') {
    return (
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={analytics.visibleEvent}
        dismissEvent={analytics.dismissEvent}
        testId="copilot-budget-request-banner"
        title="Request sent"
        variant="success"
        location={analytics.location}
        rounded={rounded}
        telemetryTags={telemetryTags}
        description={
          <span className="text-normal">
            <strong>Request sent.</strong> Your org admins will be notified that you need additional Copilot budget.
            {removeRequestUrl && (
              <>
                <br />
                <span className="d-inline-flex flex-items-center mt-1">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleRemoveRequestClick}
                    disabled={submitting}
                    data-analytics-event={analytics.clickEvent}
                  >
                    Remove request
                  </Button>
                  <span className="ml-2">You can re-request after this is reviewed.</span>
                </span>
              </>
            )}
          </span>
        }
      />
    )
  }

  if (bannerState === 'request_denied') {
    return (
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={analytics.visibleEvent}
        dismissEvent={analytics.dismissEvent}
        testId="copilot-budget-request-banner"
        title="Budget request declined"
        variant="warning"
        location={analytics.location}
        rounded={rounded}
        telemetryTags={telemetryTags}
        description={
          <span className="text-normal">
            {declinedDate ? (
              <span>
                {'Your request for additional Copilot budget was declined by an admin on '}
                <strong>{declinedDate}</strong>
                {'. '}
              </span>
            ) : (
              <span>{'Your request for additional Copilot budget was declined by an admin. '}</span>
            )}
            <Link
              href={contactUrl}
              inline
              className="Link--inTextBlock"
              onClick={() => reportIpmAction('click', telemetryTags)}
            >
              Contact an admin
            </Link>{' '}
            <span>for more details.</span>
          </span>
        }
      />
    )
  }

  // pre_request state
  if (isPersonalBudget) {
    return (
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={analytics.visibleEvent}
        dismissEvent={analytics.dismissEvent}
        testId="copilot-budget-request-banner"
        title="Copilot budget exhausted"
        variant="critical"
        location={analytics.location}
        rounded={rounded}
        telemetryTags={telemetryTags}
        description={
          <span className="text-normal">
            You&apos;ve used your full Copilot budget from <strong>{organizationName}</strong>. Access resumes on{' '}
            {resetDate}. Your org admins can increase it sooner.
          </span>
        }
        primaryAction={
          <Button onClick={handleRequestClick} disabled={submitting} data-analytics-event={analytics.clickEvent}>
            {submitting ? 'Submitting...' : 'Request more budget'}
          </Button>
        }
      />
    )
  }

  return (
    <IpmBannerBase
      ref={bannerRef}
      dismissPath={dismissPath}
      visibleEvent={analytics.visibleEvent}
      dismissEvent={analytics.dismissEvent}
      testId="copilot-budget-request-banner"
      title="Copilot budget exhausted"
      variant="critical"
      location={analytics.location}
      rounded={rounded}
      telemetryTags={telemetryTags}
      description={
        <span className="text-normal">
          Your organization <strong>{organizationName}</strong> has used its full Copilot budget. Access resumes on{' '}
          {resetDate}. Your org admins can increase it sooner.
        </span>
      }
      primaryAction={
        <Button onClick={handleRequestClick} disabled={submitting} data-analytics-event={analytics.clickEvent}>
          {submitting ? 'Submitting...' : 'Request additional budget'}
        </Button>
      }
    />
  )
}
